import TEXT from './generated/TEXT/COMMAND_LIST';
import type { Command } from './types/Command';

/**
 * Include all keyboard shortcuts in this file.
 *
 * - Pick a unique command `id`
 * - Optionally add `displayLabel` to make the command appear in the
 help modal.
 * - Optionally add `isCustomizable` to allow the command to be
 customized in settings.
 * - Optionally add an array of name strings to `FeatureFlag` to show/
 hide the display label based
 *   on the enabled value of the feature flag(s).
 * - Optionally add `keys` to define a keyboard shortcut. Use
 lowercase letters
 *   for buttons and UpperCamelCase for modifiers.
 * - Optionally add `hideInShares` to hide the command from the
 *   keyboard shortcuts help dialog in the shares context (`/share/`).
 *   All commands are still registered in shares so that components
 *   can assign handlers without errors, but not all shortcuts are
 *   functional for share reviewers. Commands marked with
 *   `hideInShares: true` will be filtered out of the dialog when
 *   `isShareContext` is set on the provider.
 */

TEXT.DEFINE({
  categories: {
    /** Keyboard shortcuts category label for locate actions */
    locate: 'Locate',
    /** Keyboard shortcuts category label for panel actions */
    panels: 'Panels',
    /** Keyboard shortcuts category label for manipulation actions */
    manipulate: 'Manipulate',
    /** Keyboard shortcuts category label for object navigation */
    objects: 'Objects',
    /** Keyboard shortcuts category label for viewing actions */
    viewing: 'Viewing',
    /** Keyboard shortcuts category label for comment actions */
    comments: 'Comments',
    /** Keyboard shortcuts category label for actions on an anchored comment pin */
    anchoredComments: 'Anchored comments',
  },
  categorySubtitles: {
    /** Subtitle under the anchored comments category in the keyboard shortcuts dialog */
    anchoredComments: 'Applies while a comment pin is focused',
  },
  labels: {
    /** Keyboard shortcut label for keyboard shortcuts help */
    keyboardShortcutsHelp: 'Keyboard shortcuts help',
    /** Keyboard shortcut label for in-project search */
    inProjectSearch: 'In-Project search',
    /** Keyboard shortcut label for quick search */
    quickSearch: 'Quick search',
    /** Keyboard shortcut label for submitting feedback */
    submitFeatureRequest: 'Submit Feedback',
    /** Keyboard shortcut label for priming project navigation panel */
    primeProjectNavigationPanel: 'Prime project navigation panel',
    /** Keyboard shortcut label for priming main panel */
    primeMainPanel: 'Prime main panel',
    /** Keyboard shortcut label for priming viewer panel */
    primeViewerPanel: 'Prime viewer panel',
    /** Keyboard shortcut label for toggling viewer panel */
    toggleViewerPanel: 'Toggle viewer panel',
    /** Keyboard shortcut label for priming details panel */
    primeDetailsPanel: 'Prime details panel',
    /** Keyboard shortcut label for toggling viewer */
    toggleViewer: 'Toggle viewer',
    /** Keyboard shortcut label for toggling details panel */
    toggleDetailsPanel: 'Toggle details panel',
    /** Keyboard shortcut label for toggling AI assistant panel */
    toggleAiAssistantPanel: 'Toggle AI assistant panel',
    /** Keyboard shortcut label for priming AI assistant panel */
    primeAiPanel: 'Prime AI assistant panel',
    /** Keyboard shortcut label for toggling floating prompt bar */
    toggleFloatingPromptBar: 'Toggle floating prompt bar',
    /** Keyboard shortcut label for renaming */
    rename: 'Rename',
    /** Keyboard shortcut label for removing rating */
    removeRating: 'Remove rating',
    /** Keyboard shortcut label for rating one star */
    rate1Star: 'Rate 1 star',
    /** Keyboard shortcut label for rating two stars */
    rate2Stars: 'Rate 2 stars',
    /** Keyboard shortcut label for rating three stars */
    rate3Stars: 'Rate 3 stars',
    /** Keyboard shortcut label for rating four stars */
    rate4Stars: 'Rate 4 stars',
    /** Keyboard shortcut label for rating five stars */
    rate5Stars: 'Rate 5 stars',
    /** Keyboard shortcut label for exiting a folder */
    exitFolder: 'Exit folder',
    /** Keyboard shortcut label for moving to asset up */
    goToAssetUp: 'Go to asset up',
    /** Keyboard shortcut label for moving up and selecting range */
    goToAssetUpAndSelectRange: 'Go to asset up and select range',
    /** Keyboard shortcut label for moving to first item */
    goToFirst: 'Go to first',
    /** Keyboard shortcut label for moving to first item and selecting range */
    goToFirstAndSelectRange: 'Go to first and select range',
    /** Keyboard shortcut label for moving to asset left */
    goToAssetLeft: 'Go to asset left',
    /** Keyboard shortcut label for moving left and selecting range */
    goToAssetLeftAndSelectRange: 'Go to asset left and select range',
    /** Keyboard shortcut label for moving right and selecting range */
    goToAssetRightAndSelectRange: 'Go to asset right and select range',
    /** Keyboard shortcut label for moving to asset right */
    goToAssetRight: 'Go to asset right',
    /** Keyboard shortcut label for moving to last item and selecting range */
    goToLastAndSelectRange: 'Go to last and select range',
    /** Keyboard shortcut label for moving to last item */
    goToLast: 'Go to last',
    /** Keyboard shortcut label for moving down and selecting range */
    goToAssetDownAndSelectRange: 'Go to asset down and select range',
    /** Keyboard shortcut label for moving to asset down */
    goToAssetDown: 'Go to asset down',
    /** Keyboard shortcut label for opening folder in asset carousel */
    openFolderInAssetCarousel: 'Open folder in the asset carousel',
    /** Keyboard shortcut label for opening asset or folder in grid */
    openAssetOrFolderInGrid: 'Open asset or folder in the grid',
    /** Keyboard shortcut label for previewing the highlighted asset or folder */
    quickLook: 'Preview',
    /** Keyboard shortcut label for opening an asset in a new tab */
    openInNewTab: 'Open in new tab',
    /** Keyboard shortcut label for selecting all */
    selectAll: 'Select all',
    /** Keyboard shortcut label for deselecting all */
    deselectAll: 'Deselect all',
    /** Keyboard shortcut label for toggling checkbox */
    toggleCheckbox: 'Toggle checkbox',
    /** Keyboard shortcut label for downloading selection */
    download: 'Download',
    /** Keyboard shortcut label for duplicating selection */
    duplicate: 'Duplicate',
    /** Keyboard shortcut label for cutting selection */
    cutSelection: 'Cut selection',
    /** Keyboard shortcut label for copying selection */
    copySelection: 'Copy selection',
    /** Keyboard shortcut label for pasting clipboard */
    pasteClipboard: 'Paste clipboard',
    /** Keyboard shortcut label for deleting selection */
    delete: 'Delete',
    /** Keyboard shortcut label for creating a folder */
    createFolder: 'Create folder',
    /** Keyboard shortcut label for uploading assets */
    uploadAssets: 'Upload Asset',
    /** Keyboard shortcut label for uploading a folder */
    uploadFolder: 'Upload Folder',
    /** Keyboard shortcut label for creating a version stack */
    createVersionStack: 'Create Version Stack',
    /** Keyboard shortcut label for comparing versions */
    compareVersions: 'Compare versions',
    /** Keyboard shortcut label for next asset */
    goToNextAsset: 'Go to next asset',
    /** Keyboard shortcut tooltip for next asset */
    goToNextAssetTooltip:
      'For time-based assets, arrow right advances 1 frame forward',
    /** Keyboard shortcut label for previous asset */
    goToPreviousAsset: 'Go to previous asset',
    /** Keyboard shortcut tooltip for previous asset */
    goToPreviousAssetTooltip:
      'For time-based assets, arrow left steps 1 frame back',
    /** Keyboard shortcut label for fullscreen toggle */
    fullscreen: 'Fullscreen',
    /** Keyboard shortcut label for quick toggle in comparison viewer */
    quickToggle: 'Quick toggle',
    /** Keyboard shortcut label for zoom in */
    zoomIn: 'Zoom in',
    /** Keyboard shortcut label for zoom out */
    zoomOut: 'Zoom out',
    /** Keyboard shortcut label for zoom fit */
    zoomFit: 'Zoom fit',
    /** Keyboard shortcut label for zoom fill */
    zoomFill: 'Zoom fill',
    /** Keyboard shortcut label for zoom to 100 percent */
    zoomToHundred: 'Zoom to 100%',
    /** Keyboard shortcut label for play or pause */
    playPause: 'Play / pause',
    /** Keyboard shortcut label for playing reverse at multiple speeds */
    playReverseMultipleSpeeds: 'Play reverse multiple speeds',
    /** Keyboard shortcut label for playing forward at multiple speeds */
    playForwardMultipleSpeeds: 'Play forward multiple speeds',
    /** Keyboard shortcut label for incrementally playing reverse at multiple speeds */
    playIncrementalReverseMultipleSpeeds:
      'Play incremental reverse multiple speeds',
    /** Keyboard shortcut label for incrementally playing forward at multiple speeds */
    playIncrementalForwardMultipleSpeeds:
      'Play incremental forward multiple speeds',
    /** Keyboard shortcut label for loop playback */
    playLoop: 'Play loop',
    /** Keyboard shortcut label for moving one frame back */
    frameBack: '1 frame back',
    /** Keyboard shortcut label for moving ten frames back */
    framesBack10: '10 frames back',
    /** Keyboard shortcut label for moving ten frames forward */
    framesForward10: '10 frames forward',
    /** Keyboard shortcut label for moving one frame forward */
    frameForward: '1 frame forward',
    /** Keyboard shortcut label for setting in point */
    markInPoint: 'Mark in point',
    /** Keyboard shortcut label for setting out point */
    markOutPoint: 'Mark out point',
    /** Keyboard shortcut label for going to in point */
    goToInPoint: 'Go to in point',
    /** Keyboard shortcut label for going to out point */
    goToOutPoint: 'Go to out point',
    /** Keyboard shortcut label for toggling guides */
    guides: 'Guides',
    /** Keyboard shortcut label for marking range */
    markRange: 'Mark range',
    /** Keyboard shortcut label for mute toggle */
    mute: 'Mute',
    /** Keyboard shortcut label for toggling transcript and captions panel */
    toggleTranscriptCaptionsPanel: 'Toggle Transcript & Captions panel',
    /** Keyboard shortcut label for increasing volume by 10 percent */
    volumeUp10Percent: 'Volume up (10%)',
    /** Keyboard shortcut label for decreasing volume by 10 percent */
    volumeDown10Percent: 'Volume down (10%)',
    /** Keyboard shortcut label for marquee zoom */
    marqueeZoom: 'Marquee zoom',
    /** Keyboard shortcut label for pan tool */
    pan: 'Pan',
    /** Keyboard shortcut label for magnify tool */
    magnify: 'Magnify',
    /** Keyboard shortcut label for previous document page */
    goToPreviousPageInDocument: 'Go to previous page in document',
    /** Keyboard shortcut label for next document page */
    goToNextPageInDocument: 'Go to next page in document',
    /** Keyboard shortcut label for scrolling up in document */
    scrollUpInDocument: 'Scroll up in document',
    /** Keyboard shortcut label for scrolling down in document */
    scrollDownInDocument: 'Scroll down in document',
    /** Keyboard shortcut label for finding in document */
    findInDocument: 'Find in document',
    /** Keyboard shortcut label for closing in-document search */
    closeInDocumentSearch: '',
    /** Keyboard shortcut label for finding in transcript */
    findInTranscript: 'Find in transcript',
    /** Keyboard shortcut label for canceling transcription edit */
    cancelTranscriptionEdit: '',
    /** Keyboard shortcut label for adding a comment */
    addComment: 'Add comment',
    /** Keyboard shortcut label for adding an anchored comment */
    addAnchoredComment: 'Add anchored comment',
    /** Keyboard shortcut label for moving an anchored comment pin left */
    movePinLeft: 'Move pin left',
    /** Keyboard shortcut label for moving an anchored comment pin right */
    movePinRight: 'Move pin right',
    /** Keyboard shortcut label for moving an anchored comment pin up */
    movePinUp: 'Move pin up',
    /** Keyboard shortcut label for moving an anchored comment pin down */
    movePinDown: 'Move pin down',
    /** Keyboard shortcut label for switching an anchored comment pin to larger steps */
    largePinSteps: 'Bigger steps (hold Shift with an arrow)',
    /** Keyboard shortcut label for switching an anchored comment pin to one pixel steps */
    precisePinSteps: 'One pixel steps (hold Option with an arrow)',
    /** Keyboard shortcut label for finishing a move of an anchored comment pin */
    finishPinMove: 'Finish moving pin',
    /** Keyboard shortcut label for cancelling a pin move */
    cancelPinMove: 'Cancel moving pin',
    /** Keyboard shortcut label for adding an annotation */
    addAnnotation: 'Add annotation',
    /** Keyboard shortcut label for replying to comment */
    replyToComment: 'Reply to comment',
    /** Keyboard shortcut label for undo annotation */
    undoAnnotation: 'Undo annotation',
    /** Keyboard shortcut label for redo annotation */
    redoAnnotation: 'Redo annotation',
  },
});

export const categoryLabel = (category: Category): string =>
  TEXT.categories[category];

export const categorySubtitle = (category: Category): string | undefined =>
  category === 'anchoredComments'
    ? TEXT.categorySubtitles.anchoredComments
    : undefined;

export type Category = keyof typeof TEXT.categories;

const PIN_ARROWS = [
  ['Left', 'ArrowLeft'],
  ['Right', 'ArrowRight'],
  ['Up', 'ArrowUp'],
  ['Down', 'ArrowDown'],
] as const;

/**
 * Keys for moving, finishing and cancelling the move of an anchored comment pin.
 * They share their keys with other commands (frame stepping, play/pause, ...),
 * so they are marked `runsFirst`: they run ahead of the others on the same key
 * wherever they are listed, and return `false` to stop the rest when the pin
 * uses the key.
 */
const getPinMoveCommands = (): Command[] => {
  const commands: Command[] = [
    {
      displayLabel: TEXT.labels.finishPinMove,
      id: 'pinPlacement.finishMove',
      keys: [' '],
      alternativeKeys: ['Enter'],
      isCustomizable: false,
      runsFirst: true,
    },
    ...PIN_ARROWS.map(
      ([name, arrow]): Command => ({
        displayLabel: TEXT.labels[`movePin${name}`],
        id: `pinPlacement.move${name}`,
        keys: [arrow],
        isCustomizable: false,
        runsFirst: true,
      })
    ),
    // Only the right arrow is listed for each step size, the others are the
    // same key with a different arrow.
    ...PIN_ARROWS.map(
      ([name, arrow]): Command => ({
        displayLabel: name === 'Right' ? TEXT.labels.largePinSteps : '',
        id: `pinPlacement.largeSteps${name}`,
        keys: ['Shift', arrow],
        isCustomizable: false,
        runsFirst: true,
      })
    ),
    ...PIN_ARROWS.map(
      ([name, arrow]): Command => ({
        displayLabel: name === 'Right' ? TEXT.labels.precisePinSteps : '',
        id: `pinPlacement.preciseSteps${name}`,
        keys: ['Alt', arrow],
        isCustomizable: false,
        runsFirst: true,
      })
    ),
    {
      displayLabel: TEXT.labels.cancelPinMove,
      id: 'pinPlacement.cancelMove',
      keys: ['Escape'],
      isCustomizable: false,
      runsFirst: true,
    },
  ];

  return commands.map((command) => ({
    ...command,
    // An empty label keeps the command out of the keyboard shortcuts dialog.
    displayLabel: '',
    featureFlag: ['v4.movable-pin'],
  }));
};

export const getCommandList = (): {
  categoryName: keyof typeof TEXT.categories;
  orderedCommands: Command[];
}[] =>
  [
    {
      categoryName: 'locate' as const,
      orderedCommands: [
        {
          displayLabel: TEXT.labels.keyboardShortcutsHelp,
          id: 'global.help',
          keys: ['?'],
        },
        {
          displayLabel: TEXT.labels.inProjectSearch,
          hideInShares: true,
          id: 'finder.inProjectSearch',
          keys: ['Meta', 'f'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.quickSearch,
          hideInShares: true,
          id: 'global.quickSearch',
          keys: ['Meta', 'k'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.submitFeatureRequest,
          hideInShares: true,
          id: 'global.featureRequest',
          keys: ['Shift', 'f'],
          isCustomizable: true,
        },
      ],
    },
    {
      categoryName: 'panels' as const,
      orderedCommands: [
        { id: 'init.directoryNav' },
        { id: 'init.directoryContent' },
        { id: 'init.viewer' },
        { id: 'init.details' },
        { id: 'init.ai' },
        {
          displayLabel: TEXT.labels.primeProjectNavigationPanel,
          hideInShares: true,
          id: 'panels.prime.directoryNav',
          keys: ['Shift', '1'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.primeMainPanel,
          id: 'panels.prime.directoryContent',
          keys: ['Shift', '2'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.primeViewerPanel,
          id: 'panels.prime.viewer',
          keys: ['Shift', '3'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.toggleViewerPanel,
          hideInShares: true,
          id: 'panels.toggle.viewer',
          keys: ['Alt', '3'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.primeDetailsPanel,
          id: 'panels.prime.details',
          keys: ['Shift', '4'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.toggleViewer,
          hideInShares: true,
          id: 'panels.toggle.defaultRow',
        },
        {
          displayLabel: TEXT.labels.toggleDetailsPanel,
          id: 'panels.toggle.details',
          keys: ['Alt', '4'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.toggleAiAssistantPanel,
          hideInShares: true,
          id: 'panels.toggle.ai',
          keys: ['Alt', '5'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.primeAiPanel,
          hideInShares: true,
          id: 'panels.prime.ai',
          keys: ['Shift', '5'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.toggleFloatingPromptBar,
          hideInShares: true,
          id: 'panels.toggle.floatingComposer',
          keys: ['Alt', 'b'],
          isCustomizable: true,
        },
      ],
    },
    {
      categoryName: 'manipulate' as const,
      orderedCommands: [
        {
          displayLabel: TEXT.labels.rename,
          hideInShares: true,
          id: 'finder.rename',
          keys: ['Shift', 'r'],
          panel: 'directoryContent',
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.removeRating,
          hideInShares: true,
          id: 'finder.clearAssetRating',
          keys: ['0'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.rate1Star,
          hideInShares: true,
          id: 'finder.setRating1',
          keys: ['1'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.rate2Stars,
          hideInShares: true,
          id: 'finder.setRating2',
          keys: ['2'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.rate3Stars,
          hideInShares: true,
          id: 'finder.setRating3',
          keys: ['3'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.rate4Stars,
          hideInShares: true,
          id: 'finder.setRating4',
          keys: ['4'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.rate5Stars,
          hideInShares: true,
          id: 'finder.setRating5',
          keys: ['5'],
          isCustomizable: true,
        },
      ],
    },
    {
      categoryName: 'objects' as const,
      orderedCommands: [
        {
          displayLabel: TEXT.labels.exitFolder,
          hideInShares: true,
          id: 'assetCarousel.exitFolder',
          keys: ['Alt', 'Enter'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.goToAssetUp,
          id: 'finder.up',
          keys: ['ArrowUp'],
          panel: 'directoryContent',
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.goToAssetUpAndSelectRange,
          id: 'finder.upAndSelect',
          keys: ['Shift', 'ArrowUp'],
          panel: 'directoryContent',
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.goToFirst,
          id: 'finder.first',
          keys: ['Meta', 'ArrowUp'],
          panel: 'directoryContent',
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.goToFirstAndSelectRange,
          id: 'finder.firstAndSelect',
          keys: ['Meta', 'Shift', 'ArrowUp'],
          panel: 'directoryContent',
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.goToAssetLeft,
          id: 'finder.grid.left',
          keys: ['ArrowLeft'],
          panel: 'directoryContent',
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.goToAssetLeftAndSelectRange,
          id: 'finder.grid.leftAndSelect',
          keys: ['Shift', 'ArrowLeft'],
          panel: 'directoryContent',
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.goToAssetRightAndSelectRange,
          id: 'finder.grid.rightAndSelect',
          keys: ['Shift', 'ArrowRight'],
          panel: 'directoryContent',
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.goToAssetRight,
          id: 'finder.grid.right',
          keys: ['ArrowRight'],
          panel: 'directoryContent',
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.goToLastAndSelectRange,
          id: 'finder.lastAndSelect',
          keys: ['Meta', 'Shift', 'ArrowDown'],
          panel: 'directoryContent',
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.goToLast,
          id: 'finder.last',
          keys: ['Meta', 'ArrowDown'],
          panel: 'directoryContent',
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.goToAssetDownAndSelectRange,
          id: 'finder.downAndSelect',
          keys: ['Shift', 'ArrowDown'],
          panel: 'directoryContent',
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.goToAssetDown,
          id: 'finder.down',
          keys: ['ArrowDown'],
          panel: 'directoryContent',
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.openFolderInAssetCarousel,
          id: 'assetCarousel.enterFolder',
          keys: ['Enter'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.openAssetOrFolderInGrid,
          id: 'finder.grid.open',
          keys: ['Enter'],
          panel: 'directoryContent',
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.quickLook,
          id: 'finder.quickLook',
          keys: [' '],
          panel: 'directoryContent',
          featureFlag: ['v4.finder_quicklook'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.openInNewTab,
          id: 'finder.openInNewTab',
          keys: ['Meta'],
          actionModifiers: ['Click'],
          panel: 'directoryContent',
        },
        {
          displayLabel: TEXT.labels.selectAll,
          id: 'finder.selectAll',
          keys: ['Meta', 'a'],
          panel: 'directoryContent',
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.deselectAll,
          id: 'finder.selectNone',
          keys: ['Meta', 'Shift', 'a'],
          panel: 'directoryContent',
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.toggleCheckbox,
          id: 'finder.selectToggle',
          keys: ['x'],
          panel: 'directoryContent',
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.download,
          id: 'finder.download',
          keys: ['Shift', 'g'],
          panel: 'directoryContent',
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.duplicate,
          hideInShares: true,
          id: 'finder.duplicate',
          keys: ['Meta', 'd'],
          panel: 'directoryContent',
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.cutSelection,
          hideInShares: true,
          id: 'finder.cutSelection',
          keys: ['Meta', 'x'],
          panel: 'directoryContent',
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.copySelection,
          hideInShares: true,
          id: 'finder.copySelection',
          keys: ['Meta', 'c'],
          panel: 'directoryContent',
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.pasteClipboard,
          hideInShares: true,
          id: 'finder.pasteClipboard',
          keys: ['Meta', 'v'],
          panel: 'directoryContent',
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.delete,
          hideInShares: true,
          id: 'finder.delete',
          keys: ['Backspace'],
          panel: 'directoryContent',
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.createFolder,
          hideInShares: true,
          id: 'finder.createFolder',
          keys: ['Shift', 'n'],
          panel: 'directoryContent',
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.uploadAssets,
          hideInShares: true,
          id: 'finder.uploadAsset',
          keys: ['u'],
          panel: 'directoryContent',
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.uploadFolder,
          hideInShares: true,
          id: 'finder.uploadFolder',
          keys: ['Shift', 'u'],
          panel: 'directoryContent',
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.createVersionStack,
          hideInShares: true,
          id: 'finder.createVersionStack',
          keys: ['Shift', 's'],
          panel: 'directoryContent',
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.compareVersions,
          id: 'finder.compareVersions',
          keys: ['s'],
          panel: 'directoryContent',
          isCustomizable: true,
        },
      ],
    },
    {
      // @todo (VIEW-4575): Add actions such as click/drag/hold
      categoryName: 'viewing' as const,
      orderedCommands: [
        {
          displayLabel: TEXT.labels.goToNextAsset,
          id: 'viewing.next',
          keys: [']'],
          alternativeKeys: ['ArrowRight'],
          isCustomizable: true,
          tooltip: TEXT.labels.goToNextAssetTooltip,
        },
        {
          displayLabel: TEXT.labels.goToPreviousAsset,
          id: 'viewing.previous',
          keys: ['['],
          alternativeKeys: ['ArrowLeft'],
          isCustomizable: true,
          tooltip: TEXT.labels.goToPreviousAssetTooltip,
        },
        {
          displayLabel: TEXT.labels.fullscreen,
          id: 'viewing.fullscreen',
          keys: ['f'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.quickToggle,
          id: 'viewing.quickToggle',
          keys: ['\\'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.compareVersions,
          id: 'viewing.compareVersions',
          keys: ['s'],
          panel: 'viewer',
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.zoomIn,
          id: 'viewing.zoomIn',
          keys: ['+'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.zoomOut,
          id: 'viewing.zoomOut',
          keys: ['-'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.zoomFit,
          id: 'viewing.zoomFit',
          keys: ['t'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.zoomFill,
          id: 'viewing.zoomFill',
          keys: ['y'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.zoomToHundred,
          id: 'viewing.zoomToHundred',
          keys: ['Meta', '0'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.playPause,
          id: 'viewing.togglePlaying',
          keys: [' '],
          alternativeKeys: ['k'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.playReverseMultipleSpeeds,
          id: 'viewing.setPreviousHotkeyPlaybackRate',
          keys: ['j'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.playForwardMultipleSpeeds,
          id: 'viewing.setNextHotkeyPlaybackRate',
          keys: ['l'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.playIncrementalReverseMultipleSpeeds,
          id: 'viewing.graduallySetPreviousHotkeyPlaybackRate',
          keys: ['Shift', 'j'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.playIncrementalForwardMultipleSpeeds,
          id: 'viewing.graduallySetNextHotkeyPlaybackRate',
          keys: ['Shift', 'l'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.playLoop,
          id: 'viewing.toggleLoop',
          keys: ['Control', 'l'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.frameBack,
          id: 'viewing.previousFrame',
          keys: ['ArrowLeft'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.framesBack10,
          id: 'viewing.decrementByFrames',
          keys: ['Shift', 'ArrowLeft'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.framesForward10,
          id: 'viewing.incrementByFrames',
          keys: ['Shift', 'ArrowRight'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.frameForward,
          id: 'viewing.nextFrame',
          keys: ['ArrowRight'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.markInPoint,
          id: 'viewing.setInPoint',
          keys: ['i'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.markOutPoint,
          id: 'viewing.setOutPoint',
          keys: ['o'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.goToInPoint,
          id: 'viewing.goToInPoint',
          keys: ['Shift', 'i'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.goToOutPoint,
          id: 'viewing.goToOutPoint',
          keys: ['Shift', 'o'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.guides,
          id: 'viewing.toggleGuides',
          keys: ['g'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.markRange,
          id: 'viewing.onRKeyDown',
          keys: ['r'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.mute,
          id: 'viewing.toggleMute',
          keys: ['m'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.toggleTranscriptCaptionsPanel,
          id: 'viewing.toggleTranscription',
          keys: ['Shift', 't'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.volumeUp10Percent,
          id: 'viewing.increaseVolume',
          keys: ['Control', '+'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.volumeDown10Percent,
          id: 'viewing.decreaseVolume',
          keys: ['Control', '-'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.marqueeZoom,
          id: 'viewing.setMarquee',
          keys: ['z'],
          actionModifiers: ['Click', 'Drag'],
        },
        {
          displayLabel: TEXT.labels.pan,
          id: 'viewing.pan',
          keys: [' '],
          actionModifiers: ['Drag'],
        },
        {
          displayLabel: TEXT.labels.magnify,
          id: 'viewing.loupe',
          keys: ['Shift'],
          actionModifiers: ['Hold'],
        },
        {
          displayLabel: TEXT.labels.goToPreviousPageInDocument,
          id: 'viewing.goToPreviousPage',
          keys: ['ArrowUp'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.goToNextPageInDocument,
          id: 'viewing.goToNextPage',
          keys: ['ArrowDown'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.scrollUpInDocument,
          id: 'viewing.scrollUp',
          keys: ['ArrowUp'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.scrollDownInDocument,
          id: 'viewing.scrollDown',
          keys: ['ArrowDown'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.findInDocument,
          id: 'viewing.inDocumentSearch',
          keys: ['Meta', 'f'],
          isCustomizable: true,
        },
        {
          displayLabel: '',
          id: 'viewing.closeInDocumentSearch',
          keys: ['Escape'],
        },
        {
          displayLabel: TEXT.labels.findInTranscript,
          id: 'viewing.inTranscriptSearch',
          keys: ['Meta', 'f'],
          isCustomizable: true,
        },
        {
          displayLabel: '',
          id: 'viewing.cancelTranscriptionEdit',
          keys: ['Escape'],
        },
      ],
    },
    {
      categoryName: 'comments' as const,
      orderedCommands: [
        {
          displayLabel: TEXT.labels.addComment,
          id: 'comments.addComment',
          keys: ['c'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.addAnchoredComment,
          id: 'comments.addAnchoredComment',
          keys: ['Shift', 'c'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.addAnnotation,
          id: 'comments.addAnnotation',
          keys: ['p'],
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.replyToComment,
          id: 'comments.replyToComment',
          keys: ['Shift', 'r'],
          panel: 'details',
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.undoAnnotation,
          id: 'comments.undo',
          keys: ['Meta', 'z'],
          panel: 'details',
          isCustomizable: true,
        },
        {
          displayLabel: TEXT.labels.redoAnnotation,
          id: 'comments.redo',
          keys: ['Shift', 'Meta', 'z'],
          panel: 'details',
          isCustomizable: true,
        },
      ],
    },
    {
      categoryName: 'anchoredComments' as const,
      orderedCommands: getPinMoveCommands(),
    },
  ] as const;

export type CommandList = ReturnType<typeof getCommandList>;
